import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from "react";
import { gql, useReactiveVar } from "@apollo/client";
import { useMarkFulfillmentFulfilled, useArchiveFulfillment } from "../cache/FulfillmentLineFunctions";
import CopyToClipboard from "react-copy-to-clipboard";
import Button, { ButtonVariant } from "./Button";
import { ModalConfirmation } from "../../index";
import { fulfillmentScreenSendNotificationVar } from "../cache/FulfillmentScreenFunctions";
import { useAppBridge } from "@shopify/app-bridge-react";
import { Redirect } from "@shopify/app-bridge/actions";
import { activeFulfillmentIdVar } from "../helpers/client";
import { DateTime } from "luxon";
import { FaCheckCircle } from "react-icons/fa";
import { benefitFulfillmentText } from "../helpers/tools";
gql `
  fragment FulfillmentLineAddress on Address {
    shippingName
    line1
    line2
    city
    state
    zip
    country
  }

  fragment FulfillmentLineAddons on Addon {
    id
    description
    platformId
  }

  fragment FulfillmentLine on Fulfillment {
    id
    isFulfilled
    isArchived
    isGift
    fulfillmentDate
    shopifyUrl
    platformIdKind
    extraCharge {
      amount
    }
    membership {
      tier {
        name
      }
      plan {
        price {
          amount
        }
      }
    }
    benefit {
      id
      name
      ... on SubscriptionBenefit {
        shippingIncluded
        deliveredToYour
        isAddonOnly
      }
    }
    address {
      ...FulfillmentLineAddress
    }
    shippingNotes
    addons {
      ...FulfillmentLineAddons
    }
    productSelections {
      variant {
        id
        description
        platformId
        platformName 
      }
    }
    variant {
      id
      description
      platformId
      platformName
    }
  }
`;
const FulfillmentLine = ({ data }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u;
    let app, redirect = null;
    let isEmbedded = false;
    try {
        app = useAppBridge();
        redirect = Redirect.create(app);
        isEmbedded = true;
    }
    catch (e) { }
    const missingAddress = !(!!((_a = data.fulfillment.address) === null || _a === void 0 ? void 0 : _a.line1) && !!((_b = data.fulfillment.address) === null || _b === void 0 ? void 0 : _b.city) && !!((_c = data.fulfillment.address) === null || _c === void 0 ? void 0 : _c.state) && !!((_d = data.fulfillment.address) === null || _d === void 0 ? void 0 : _d.zip) && !!((_e = data.fulfillment.address) === null || _e === void 0 ? void 0 : _e.country));
    const { wrapped: markFulfillmentFulfilled } = useMarkFulfillmentFulfilled(data.fulfillment.id);
    const { wrapped: archiveFulfillment } = useArchiveFulfillment(data.fulfillment.id);
    const [showModal, setShowModal] = useState(false);
    const [showArchiveModal, setShowArchiveModal] = useState(false);
    const fulfillmentScreenSendNotification = useReactiveVar(fulfillmentScreenSendNotificationVar);
    const addressString = (address) => {
        return `${address.shippingName ? `${address.shippingName}\n` : ""}${address.line1}${address.line2 ? `\n${address.line2}` : ""}
${address.city}, ${address.state} ${address.zip}
${address.country}`;
    };
    const reallyMarkAsFulfilled = () => {
        setShowModal(true);
    };
    const confirmMarkAsFulfilled = () => {
        markFulfillmentFulfilled();
        setShowModal(false);
    };
    const reallyArchiveFulfillment = () => {
        setShowArchiveModal(true);
    };
    const confirmArchiveFulfillment = () => {
        archiveFulfillment();
        setShowArchiveModal(false);
    };
    // KGAUDIT - addon text may be weird - SKIPPED
    const viewInShopify = () => {
        redirect ?
            redirect.dispatch(Redirect.Action.REMOTE, { url: data.fulfillment.shopifyUrl, newContext: true }) :
            window.open(data.fulfillment.shopifyUrl);
        return false;
    };
    const confirmMarkAsFulfilledModal = (_jsxs(ModalConfirmation, { message: "", buttonConfirm: confirmMarkAsFulfilled, buttonConfirmContents: `Confirm`, buttonReject: () => setShowModal(false), buttonRejectContents: `Cancel`, children: [_jsxs("div", { className: "my-4 flex", children: ["This will charge any add-ons that this member has purchased", data.mode == 'shopify'
                        ? ", and mark the order as fulfilled on Shopify"
                        : "."] }), _jsxs("div", { className: "my-4 flex", children: [_jsx("input", { className: "mr-8 inline-block self-center", type: "checkbox", id: "fulfillment-line-send-notification", value: fulfillmentScreenSendNotification ? "true" : "false", checked: fulfillmentScreenSendNotification, onChange: (ev) => { fulfillmentScreenSendNotificationVar(ev.target.checked); } }), _jsx("label", { className: "inline-block font-sans normal-case", htmlFor: "fulfillment-line-send-notification", children: "Send a notification letting this member know their order is on the way." })] })] }));
    const confirmArchiveFulfillmentModal = (_jsx(ModalConfirmation, { message: "", buttonConfirm: confirmArchiveFulfillment, buttonConfirmContents: `Confirm`, buttonReject: () => setShowArchiveModal(false), buttonRejectContents: `Cancel`, children: _jsx("div", { className: "my-4 flex", children: "This will archive the fulfillment and hide it from you and your member. It cannot be undone." }) }));
    return (_jsxs("div", { className: "my-4 md:grid md:grid-cols-12 md:gap-2 text-wfGray-800", children: [showModal ? confirmMarkAsFulfilledModal : (showArchiveModal ? confirmArchiveFulfillmentModal : null), _jsxs("div", { className: "md:col-span-4 mr-4 pr-4 border-wfGray-300", children: [_jsxs("div", { children: [benefitFulfillmentText(data.fulfillment.benefit)('fulfillment_date'), ": ", DateTime.fromSQL(data.fulfillment.fulfillmentDate).toLocaleString(DateTime.DATE_FULL), _jsx("br", {})] }), _jsx("div", { className: "text-wfGray-800 text-sm mt-2", children: ((_f = data.fulfillment.variant) === null || _f === void 0 ? void 0 : _f.description) || ((_g = data.fulfillment.productSelections) === null || _g === void 0 ? void 0 : _g.some(productSelection => { var _a; return (_a = productSelection.variant) === null || _a === void 0 ? void 0 : _a.description; })) ? (_jsxs(_Fragment, { children: [(_h = data.fulfillment.benefit) === null || _h === void 0 ? void 0 : _h.name, ": ", data.fulfillment.productSelections
                                    .filter(productSelection => !!productSelection.variant)
                                    .map(productSelection => productSelection.variant.description)
                                    .concat(data.fulfillment.variant ? [data.fulfillment.variant.description] : [])
                                    .join(", ")] })) : ((_j = data.fulfillment.benefit) === null || _j === void 0 ? void 0 : _j.name) }), !!data.fulfillment.addons.length && (_jsxs("ul", { className: "text-wfGray-800 text-sm mt-2", children: [_jsx("li", { className: "text-wfGray-800", children: "Addons:" }), data.fulfillment.addons.map((addon) => {
                                return _jsx("li", { className: "text-wfGray-800", children: addon.description }, addon.id);
                            })] }))] }), _jsxs("div", { className: "mt-4 md:mt-0 mb-2 md:col-span-4 md:pl-6 md:pr-10 md:border-l", children: [_jsx("div", { className: "border-wfGray-300 relative border p-2", children: !((_k = data.fulfillment.address) === null || _k === void 0 ? void 0 : _k.line1) &&
                            !((_l = data.fulfillment.address) === null || _l === void 0 ? void 0 : _l.city) &&
                            !((_m = data.fulfillment.address) === null || _m === void 0 ? void 0 : _m.state) ? (_jsx("span", { children: "No address available." })) : (_jsxs(_Fragment, { children: [_jsx("div", { children: (_o = data.fulfillment.address) === null || _o === void 0 ? void 0 : _o.shippingName }), _jsx("div", { children: (_p = data.fulfillment.address) === null || _p === void 0 ? void 0 : _p.line1 }), _jsx("div", { children: (_q = data.fulfillment.address) === null || _q === void 0 ? void 0 : _q.line2 }), _jsxs("div", { children: [(_r = data.fulfillment.address) === null || _r === void 0 ? void 0 : _r.city, ", ", (_s = data.fulfillment.address) === null || _s === void 0 ? void 0 : _s.state, " ", (_t = data.fulfillment.address) === null || _t === void 0 ? void 0 : _t.zip] }), _jsx("div", { children: (_u = data.fulfillment.address) === null || _u === void 0 ? void 0 : _u.country }), data.fulfillment.address ? (_jsx("div", { className: "absolute hover:bg-wfGray-300 p-1 right-0 bottom-0 bg-wfGray-200", children: _jsx(CopyToClipboard, { text: addressString(data.fulfillment.address), children: _jsx("div", { className: "text-sm lowercase inline-block cursor-pointer hover:underline", children: "Copy" }) }) })) : null] })) }), _jsx("div", { className: "p-2", children: data.fulfillment.shippingNotes })] }), _jsxs("div", { className: "mt-4 md:mt-0 md:col-span-4 md:pl-12 md:pr-8", children: [data.fulfillment.isFulfilled ? (_jsx("div", { className: "mb-4", children: _jsxs("span", { className: "text-salmon-600 font-bold flex items-center", children: [_jsx(FaCheckCircle, { className: "mr-2" }), "Fulfilled!"] }) })) :
                        _jsxs(_Fragment, { children: [(data.mode != 'shopify' ? (_jsx("div", { children: _jsx(Button, { variant: ButtonVariant.Outlined, onClick: reallyMarkAsFulfilled, className: "mb-4 text-wfGray-800 cursor-pointer hover:underline", children: "Mark as fulfilled" }) })) : null), _jsx("div", { children: _jsx(Button, { variant: ButtonVariant.Outlined, disabled: !!data.fulfillment.shopifyUrl, disabledError: "Cannot be archived once it has been sent to Shopify", onClick: reallyArchiveFulfillment, className: "mb-4 text-wfGray-800" + (!data.fulfillment.shopifyUrl ? " cursor-pointer hover:underline" : ""), children: "Archive" }) })] }), data.fulfillment.shopifyUrl ? (_jsx("div", { children: _jsx(Button, { variant: ButtonVariant.Outlined, onClick: viewInShopify, className: "mb-4 text-wfGray-800 cursor-pointer hover:underline", children: "View in Shopify" }) })) : (null), !data.fulfillment.isFulfilled ? (_jsx("div", { children: _jsx(Button, { variant: ButtonVariant.Outlined, disabled: data.fulfillment.platformIdKind == 'Order', disabledError: "Cannot be edited after the shipment date", onClick: () => activeFulfillmentIdVar(data.fulfillment.id), className: "mb-4" + (data.fulfillment.platformIdKind != 'Order' ? " cursor-pointer hover:underline" : ""), children: "Change settings" }) })) : null] })] }));
};
export default FulfillmentLine;
